import React, { useState, useEffect, useMemo, useCallback } from 'react';
import HeaderLogged from './components/header_logged';
import Icons from './components/icons'; // SVG sprite: EmployeePopup's close button uses #closeBtn
import EmployeePopup from './components/sets/EmployeePopup';
import WorkReportTable from './components/WorkReportTable';
import WorkPerformanceStats from './components/WorkPerformanceStats';
import PackersReport from './components/PackersReport';
import AttendanceTab from './components/AttendanceTab';
import api from './api/client';
import { usePermissions } from './PermissionsContext';
import './PlanToProducePage.css';
import './ProductionPlanPage.css';
import './WorkPerformancePage.css';

// Work Performance — page-local chrome (tabs, search, filters, add) in the Production Plan
// style. Tab bodies: own Employees table, WorkReportTable (Daily Reports), PackersReport,
// WorkPerformanceStats, AttendanceTab. Search and the employee filter are passed down and applied client-side.

const TABS = [['employees', 'Employees'], ['daily', 'Daily Reports'], ['packers', 'Packers Report'], ['performance', 'Performance'], ['attendance', 'Attendance']];
const SEARCH_HINT = { employees: 'Search by name, email, phone…', daily: 'Search by employee…', packers: 'Search by employee, product, station, notes…', performance: 'Search by employee…', attendance: 'Search by employee…' };
const fmtDate = v => {
  if (!v) return '';
  const s = v instanceof Date ? v.toISOString() : String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
};
const fullName = e => [e.employeeName, e.employeeSurname].filter(Boolean).join(' ');
const readTab = () => { try { return localStorage.getItem('wp_tab') || 'employees'; } catch { return 'employees'; } };

const WorkPerformancePage = () => {
  const { can } = usePermissions();
  const canSeeRate = can('finance');      // rate — finance right; SSN — user admins. The server strips both anyway.
  const canSeeSSN = can('admin-users');
  const [tab, setTabState] = useState(readTab);
  const [search, setSearch] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [updateKey, setUpdateKey] = useState(0);
  const [empPopup, setEmpPopup] = useState({ open: false, edit: null });
  const [addReport, setAddReport] = useState(false);

  const setTab = t => { setTabState(t); try { localStorage.setItem('wp_tab', t); } catch { /* private mode */ } };
  const bump = useCallback(() => setUpdateKey(k => k + 1), []);

  useEffect(() => {
    api.get('/api/employees', { params: { limit: 1000 } })
      .then(r => setEmployees((r.data && r.data.items) || []))
      .catch(err => console.error('Error loading employees:', err));
  }, [updateKey]);

  const activeEmployees = useMemo(() => employees.filter(e => Number(e.isActive) !== 0).sort((a, b) => fullName(a).localeCompare(fullName(b))), [employees]);
  const q = search.trim().toLowerCase();
  const employeeRows = useMemo(() => employees
    .filter(e => !activeOnly || Number(e.isActive) !== 0)
    .filter(e => !q || [fullName(e), e.email, e.phone].some(v => v && String(v).toLowerCase().includes(q)))
    .sort((a, b) => (Number(b.isActive) !== 0) - (Number(a.isActive) !== 0) || fullName(a).localeCompare(fullName(b))),
  [employees, activeOnly, q]);

  return (
    <div className="pp-root wp-root">
      <Icons />
      <HeaderLogged />
      <div className="ptp-container">
        <div className="ptp-page-header">
          <div>
            <h2 className="ptp-title">Work Performance</h2>
            <p className="ptp-subtitle">Employees · Daily Reports · Packers Report · Performance · Attendance</p>
          </div>
          <div className="wp-actions">
            {tab === 'employees' && <button className="pp-btn pp-btn-primary" onClick={() => setEmpPopup({ open: true, edit: null })}><i className="fas fa-plus" />Employee</button>}
            {tab === 'daily' && <button className="pp-btn pp-btn-primary" onClick={() => setAddReport(true)}><i className="fas fa-plus" />Daily report</button>}
          </div>
        </div>

        <div className="wp-tabs">
          {TABS.map(([k, l]) => <button key={k} className={`wp-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{l}</button>)}
        </div>

        <div className="pp-bar wp-bar">
          <div className="ptp-search-wrapper">
            <i className="fas fa-search ptp-search-icon" />
            <input className="ptp-search-input" type="text" placeholder={SEARCH_HINT[tab]} value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="ptp-search-clear" onClick={() => setSearch('')} title="Clear"><i className="fas fa-times" /></button>}
          </div>
          {tab !== 'employees' && (
            <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
              <option value="">All employees</option>
              {activeEmployees.map(e => <option key={e.employeeID} value={e.employeeID}>{fullName(e)}</option>)}
            </select>
          )}
          {tab === 'employees' && (
            <button className={`pp-tgl${activeOnly ? ' on' : ''}`} onClick={() => setActiveOnly(v => !v)}>Active only</button>
          )}
          {tab === 'employees' && <span className="pp-muted wp-count">{employeeRows.length} of {employees.length} employees</span>}
        </div>

        {tab === 'employees' && (
          <EmployeesTable rows={employeeRows} canSeeRate={canSeeRate} canSeeSSN={canSeeSSN} onEdit={e => setEmpPopup({ open: true, edit: e })} />
        )}
        {tab === 'daily' && (
          <WorkReportTable updateKey={updateKey} search={q} employeeId={employeeId} externalAdd={addReport} onExternalAddHandled={() => setAddReport(false)} />
        )}
        {tab === 'packers' && <PackersReport search={q} employeeId={employeeId} />}
        {tab === 'performance' && <WorkPerformanceStats updateKey={updateKey} search={q} employeeId={employeeId} />}
        {tab === 'attendance' && <AttendanceTab search={q} employeeId={employeeId} employees={activeEmployees} />}
      </div>

      <EmployeePopup isOpen={empPopup.open} editData={empPopup.edit}
                     onClose={() => setEmpPopup({ open: false, edit: null })} onSaved={bump} />
    </div>
  );
};

const EmployeesTable = ({ rows, canSeeRate, canSeeSSN, onEdit }) => (
  <div className="wp-table-wrap">
    <table className="wp-table">
      <thead>
        <tr>
          <th>Employee</th><th>Gender</th><th>Birth date</th><th>Hire date</th><th>Email</th><th>Phone</th>
          {canSeeRate && <th className="wp-num">Rate $/h</th>}
          {canSeeSSN && <th>SSN</th>}
          <th>Status</th><th />
        </tr>
      </thead>
      <tbody>
        {!rows.length && <tr><td colSpan={10} className="wp-empty">No employees match</td></tr>}
        {rows.map(e => {
          const active = Number(e.isActive) !== 0;
          return (
            <tr key={e.employeeID} className={active ? '' : 'wp-inactive'} onClick={() => onEdit(e)}>
              <td className="wp-cell-name"><span className="wp-avatar">{(e.employeeName || '?').charAt(0).toUpperCase()}</span><span className="wp-name">{fullName(e)}</span></td>
              <td data-label="Gender">{e.gender || ''}</td>
              <td data-label="Birth date">{fmtDate(e.birthDate)}</td>
              <td data-label="Hire date">{fmtDate(e.hireDate)}</td>
              <td data-label="Email">{e.email || ''}</td>
              <td data-label="Phone">{e.phone || ''}</td>
              {canSeeRate && <td className="wp-num" data-label="Rate $/h">{e.rate != null && e.rate !== '' ? Number(e.rate).toFixed(2) : ''}</td>}
              {canSeeSSN && <td data-label="SSN">{e.socialSecurityNumber || ''}</td>}
              <td data-label="Status"><span className={`wp-badge ${active ? 'wp-badge--on' : 'wp-badge--off'}`}>{active ? 'Active' : 'Inactive'}</span></td>
              <td className="wp-cell-edit"><button className="wp-edit" title="Edit" onClick={ev => { ev.stopPropagation(); onEdit(e); }}><i className="fas fa-pen" /></button></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default WorkPerformancePage;
